import React, { useEffect, useState, useCallback, useReducer, useRef } from 'react';
import type { PaginationMeta } from '../types/startup';
import { useTranslation } from 'react-i18next';
import { useDebouncedSearch, captureException, getPageNumbers } from '@startus/lib';
import { getUserErrorMessage } from '../utils/userErrorMessage';
import { usersService } from '../services/users.service';
import { investorsService } from '../services/investors.service';
import type { CreateHubMemberPayload, SetUserInvestorMembershipResult } from '../types/investor';
import { referenceCodesService } from '../services/referenceCodes.service';
import type { ReferenceCode } from '../types/referenceCode';
import type { User, CreateUserPayload, UpdateUserPayload } from '../types/user';
import UsersTable from '../components/UsersTable';
import UserFormModal from '../components/UserFormModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { Toast } from '@startus/ui';

type ToastState = {
  message: string;
  type: 'success' | 'error';
} | null;

type State = {
  users: User[];
  meta: PaginationMeta | null;
  referenceCodeCount: number | null;
  loading: boolean;
  selectedUser: User | null;
  showFormModal: boolean;
  showDeleteModal: boolean;
  toast: ToastState;
};

type Action =
  | { type: 'FETCH_USERS_START' }
  | {
      type: 'FETCH_USERS_SUCCESS';
      payload: { users: User[]; meta: PaginationMeta; referenceCodeCount?: number };
    }
  | { type: 'FETCH_USERS_ERROR'; payload: string }
  | { type: 'SET_SELECTED_USER'; payload: User | null }
  | { type: 'SET_SHOW_FORM_MODAL'; payload: boolean }
  | { type: 'SET_SHOW_DELETE_MODAL'; payload: boolean }
  | { type: 'SET_TOAST'; payload: ToastState };

const initialState: State = {
  users: [],
  meta: null,
  referenceCodeCount: null,
  loading: true,
  selectedUser: null,
  showFormModal: false,
  showDeleteModal: false,
  toast: null,
};

const usersReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_USERS_START':
      return { ...state, loading: true };
    case 'FETCH_USERS_SUCCESS':
      return {
        ...state,
        loading: false,
        users: action.payload.users,
        meta: action.payload.meta,
        referenceCodeCount: action.payload.referenceCodeCount ?? null,
      };
    case 'FETCH_USERS_ERROR':
      return { ...state, loading: false, toast: { message: action.payload, type: 'error' } };
    case 'SET_SELECTED_USER':
      return { ...state, selectedUser: action.payload };
    case 'SET_SHOW_FORM_MODAL':
      return { ...state, showFormModal: action.payload };
    case 'SET_SHOW_DELETE_MODAL':
      return { ...state, showDeleteModal: action.payload };
    case 'SET_TOAST':
      return { ...state, toast: action.payload };
    default:
      return state;
  }
};

const PAGE_SIZE = 10;

type SortOption = {
  labelKey: string;
  orderBy: 'name' | 'lastname' | 'role' | 'lastLoginAt' | 'createdAt';
  sortDir: 'asc' | 'desc';
};

const SORT_OPTIONS: SortOption[] = [
  { labelKey: 'users.sort.newest', orderBy: 'createdAt', sortDir: 'desc' },
  { labelKey: 'users.sort.oldest', orderBy: 'createdAt', sortDir: 'asc' },
  { labelKey: 'users.sort.nameAsc', orderBy: 'name', sortDir: 'asc' },
  { labelKey: 'users.sort.nameDesc', orderBy: 'name', sortDir: 'desc' },
  { labelKey: 'users.sort.lastLogin', orderBy: 'lastLoginAt', sortDir: 'desc' },
];

const UsersPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [state, dispatch] = useReducer(usersReducer, initialState);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedSearch(searchTerm);
  const [referenceCodeFilter, setReferenceCodeFilter] = useState('');
  const [referenceCodes, setReferenceCodes] = useState<ReferenceCode[]>([]);
  // The filter still works without the catalog ("Todos" only), but the admin must know why.
  const [referenceCodesFailed, setReferenceCodesFailed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSortIndex, setActiveSortIndex] = useState(0);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const fetchUsers = useCallback(async () => {
    const { orderBy, sortDir } = SORT_OPTIONS[activeSortIndex];
    dispatch({ type: 'FETCH_USERS_START' });
    try {
      const result = await usersService.getUsers({
        page: currentPage,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        orderBy,
        sortDir,
        referenceCode: referenceCodeFilter || undefined,
      });
      dispatch({
        type: 'FETCH_USERS_SUCCESS',
        payload: {
          users: result.data,
          meta: result.meta,
          referenceCodeCount: result.referenceCodeCount,
        },
      });
    } catch (error) {
      captureException(error);
      dispatch({ type: 'FETCH_USERS_ERROR', payload: getUserErrorMessage(error, t, 'fetch') });
    }
  }, [currentPage, debouncedSearch, activeSortIndex, referenceCodeFilter, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    referenceCodesService
      .getReferenceCodes()
      .then(setReferenceCodes)
      .catch((err: unknown) => {
        captureException(err, { feature: 'admin-users', action: 'load-reference-codes' });
        setReferenceCodesFailed(true);
      });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setShowSort(false);
    };
    if (showSort) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSort]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setShowFilter(false);
    };
    if (showFilter) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilter]);

  const handleCreateUser = async (payload: CreateUserPayload) => {
    try {
      await usersService.createUser(payload);
      await fetchUsers();
      dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: false });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('users.success.created'), type: 'success' },
      });
    } catch (error: unknown) {
      captureException(error);
      dispatch({
        type: 'SET_TOAST',
        payload: { message: getUserErrorMessage(error, t, 'create'), type: 'error' },
      });
    }
  };

  const handleEditUser = async (payload: UpdateUserPayload) => {
    try {
      const { isActive, ...rest } = payload;
      // Only include isActive when it actually changed — service calls /restore on isActive===true
      const finalPayload: UpdateUserPayload =
        isActive !== state.selectedUser?.isActive ? { ...rest, isActive } : rest;
      await usersService.updateUser(finalPayload);
      await fetchUsers();
      dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: false });
      dispatch({ type: 'SET_SELECTED_USER', payload: null });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('users.success.updated'), type: 'success' },
      });
    } catch (error: unknown) {
      captureException(error);
      dispatch({
        type: 'SET_TOAST',
        payload: { message: getUserErrorMessage(error, t, 'update'), type: 'error' },
      });
    }
  };

  // Creates a new user as a member of an existing investor (Hub access).
  const handleCreateHubMember = async (investorId: string, payload: CreateHubMemberPayload) => {
    try {
      await investorsService.createHubMember(investorId, payload);
      await fetchUsers();
      dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: false });
      dispatch({
        type: 'SET_TOAST',
        payload: {
          // No password → backend emailed a magic link; tell the admin which path happened.
          message: t(payload.password ? 'users.success.hubCreated' : 'users.success.hubInvited'),
          type: 'success',
        },
      });
    } catch (error: unknown) {
      captureException(error, { investorId, role: payload.role });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: getUserErrorMessage(error, t, 'create'), type: 'error' },
      });
    }
  };

  // After the edit modal changes the user's investor. A deleted user (Hub-only,
  // investor removed) closes the modal; the toast is visible once it's gone.
  const handleMembershipApplied = async (result: SetUserInvestorMembershipResult) => {
    if (result.userDeleted) {
      dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: false });
      dispatch({ type: 'SET_SELECTED_USER', payload: null });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('users.membership.userDeleted'), type: 'success' },
      });
    }
    await fetchUsers();
  };

  const handleSubmit = async (payload: CreateUserPayload | UpdateUserPayload) => {
    if ('id' in payload) {
      await handleEditUser(payload);
    } else {
      await handleCreateUser(payload);
    }
  };

  const handleDeleteUser = async () => {
    const userId = state.selectedUser?.id;
    if (!userId) return;
    try {
      await usersService.deleteUser(userId);
      await fetchUsers();
      dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: false });
      dispatch({ type: 'SET_SELECTED_USER', payload: null });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('users.success.deleted'), type: 'success' },
      });
    } catch (error) {
      captureException(error);
      dispatch({
        type: 'SET_TOAST',
        payload: { message: getUserErrorMessage(error, t, 'delete'), type: 'error' },
      });
    }
  };

  const openNewUserModal = () => {
    dispatch({ type: 'SET_SELECTED_USER', payload: null });
    dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: true });
  };

  const openEditUserModal = (user: User) => {
    dispatch({ type: 'SET_SELECTED_USER', payload: user });
    dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: true });
  };

  const openDeleteUserModal = (user: User) => {
    dispatch({ type: 'SET_SELECTED_USER', payload: user });
    dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: true });
  };

  const closeModal = () => {
    dispatch({ type: 'SET_SHOW_FORM_MODAL', payload: false });
    dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: false });
    dispatch({ type: 'SET_SELECTED_USER', payload: null });
  };

  if (state.loading && !state.users.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl">
      {state.toast && (
        <Toast
          message={state.toast.message}
          type={state.toast.type}
          onClose={() => dispatch({ type: 'SET_TOAST', payload: null })}
          duration={3000}
        />
      )}
      <div className="card bg-[#1E1E1E] shadow-xl">
        <div className="card-body">
          <h1 className="card-title text-2xl font-bold">{t('users.title')}</h1>
          <p className="text-base-content/70">{t('users.subtitle')}</p>

          <div className="flex items-end gap-2 mb-4">
            <div className="form-control w-full max-w-xs">
              <label className="label" htmlFor="users-search">
                <span className="label-text">{t('users.search.label')}</span>
              </label>
              <input
                id="users-search"
                type="text"
                placeholder={t('users.search.placeholder')}
                className="input input-bordered"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div ref={sortRef} className="relative">
              <button
                type="button"
                className={`btn btn-ghost btn-square${activeSortIndex !== 0 ? ' text-primary' : ''}`}
                aria-label={t('users.sort.label')}
                onClick={() => setShowSort((v) => !v)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </button>

              {showSort && (
                <div className="absolute right-0 top-full mt-1 bg-[#1E1E1E] border border-base-300 rounded-2xl shadow-xl z-20 min-w-65 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-sm">{t('users.sort.label')}</span>
                    <button
                      type="button"
                      className="text-base-content/60 hover:text-base-content transition-colors"
                      onClick={() => setShowSort(false)}
                      aria-label={t('common.close', { ns: 'common' })}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="flex flex-col gap-1">
                    {SORT_OPTIONS.map((opt, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          idx === activeSortIndex
                            ? 'bg-primary/20 text-primary font-medium'
                            : 'bg-base-300 hover:bg-base-200'
                        }`}
                        onClick={() => {
                          setActiveSortIndex(idx);
                          setCurrentPage(1);
                          setShowSort(false);
                        }}
                      >
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Filter */}
            <div ref={filterRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowFilter((v) => !v);
                  setShowSort(false);
                }}
                className={`btn btn-ghost btn-square ${referenceCodeFilter ? 'text-primary' : ''}`}
                aria-label={t('users.filters.label')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>

              {showFilter && (
                <div className="absolute right-0 top-full mt-1 bg-base-100 border border-base-200 rounded-lg shadow-lg z-20 w-72 p-3 space-y-3">
                  {/* Reference code */}
                  <div>
                    <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-1.5">
                      {t('users.filters.referenceCode')}
                    </p>
                    <select
                      value={referenceCodeFilter}
                      onChange={(e) => {
                        setReferenceCodeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className={`select select-bordered select-sm w-full ${referenceCodeFilter ? 'select-primary' : ''}`}
                    >
                      <option value="">{t('users.filters.any')}</option>
                      {referenceCodes.map((rc) => (
                        <option key={rc.id} value={rc.code}>
                          {rc.code}
                        </option>
                      ))}
                    </select>
                    {referenceCodesFailed && (
                      <p role="alert" className="text-xs text-error mt-1">
                        {t('users.filters.referenceCodesError')}
                      </p>
                    )}
                  </div>

                  {/* Clear */}
                  {referenceCodeFilter && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs w-full"
                      onClick={() => {
                        setReferenceCodeFilter('');
                        setCurrentPage(1);
                      }}
                    >
                      {t('users.filters.clearAll')}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Active filter chip */}
            {referenceCodeFilter && state.referenceCodeCount !== null && (
              <div className="flex items-center gap-1.5 self-end px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium">
                <span>
                  {t('users.filters.userCount', {
                    count: state.referenceCodeCount,
                    code: referenceCodeFilter,
                  })}
                </span>
                <button
                  type="button"
                  className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
                  aria-label={t('users.filters.clearAll')}
                  onClick={() => {
                    setReferenceCodeFilter('');
                    setCurrentPage(1);
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="mb-4">
            <button className="btn btn-primary" onClick={openNewUserModal}>
              {t('users.add')}
            </button>
          </div>

          {state.loading && <span className="loading loading-spinner loading-sm"></span>}

          {!state.loading && state.users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-lg">{t('users.empty')}</p>
            </div>
          ) : (
            <>
              <UsersTable
                users={state.users}
                onEdit={openEditUserModal}
                onDelete={openDeleteUserModal}
              />
              {state.meta && state.meta.totalPages > 1 && (
                <div className="flex justify-center items-center gap-1 mt-4">
                  <button
                    className="px-2 text-base-content/50 hover:text-base-content disabled:opacity-25 disabled:cursor-not-allowed"
                    disabled={!state.meta.hasPrevious}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    aria-label={t('pagination.previous')}
                  >
                    &lt;
                  </button>
                  {getPageNumbers(currentPage, state.meta.totalPages).map((page, i) =>
                    page === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-1 text-base-content/40 select-none">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`px-2 min-w-[1.75rem] text-sm rounded hover:text-base-content${page === currentPage ? ' font-bold text-base-content' : ' text-base-content/50'}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    className="px-2 text-base-content/50 hover:text-base-content disabled:opacity-25 disabled:cursor-not-allowed"
                    disabled={!state.meta.hasNext}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    aria-label={t('pagination.next')}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <UserFormModal
        key={state.showFormModal ? (state.selectedUser?.id ?? 'new') : undefined}
        open={state.showFormModal}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onSubmitHubMember={handleCreateHubMember}
        onMembershipApplied={handleMembershipApplied}
        user={state.selectedUser}
      />
      <ConfirmDeleteModal
        open={state.showDeleteModal}
        onClose={closeModal}
        onConfirm={handleDeleteUser}
        userName={
          state.selectedUser
            ? `${state.selectedUser.name} ${state.selectedUser.lastname}`
            : undefined
        }
      />
    </div>
  );
};
export default UsersPage;
